import React, { useEffect, useState } from 'react';
import { 
  Box, 
  TextField, 
  CircularProgress, 
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, getApiUrl } from '../../config/api';
import { getDeviceId } from '../../utils/deviceId';
import { useContract } from '../../context/ContractContext';
import { useNotification } from '../../context/NotificationContext';
import { primusAPI } from '../../services/backendAPI/request';
import { EXCHANGE_CODES } from '../../websocket/constants';
import { format } from 'date-fns';
import ExchangeTag from '../Common/ExchangeTag';

const SearchBar = ({
  marketWatches,
  activeTab,
  // formatExpiryDate,
  setMarketWatches,
  error,
  setError,
  addInstrument,
  setHighlightedInstrument,
  currentWatchlist,
  isExpandedView = false
}) => {

      const [searchQuery, setSearchQuery] = useState('');
      const [searchResults, setSearchResults] = useState([]);
      const [searching, setSearching] = useState(false);
      const { updateContract } = useContract();
      const { showNotification } = useNotification();
      const navigate = useNavigate();

      const formatExpiryDate = (epochTimestamp) => {
        try {
          // Convert epoch to milliseconds if it's in seconds
          const timestamp = epochTimestamp.toString().length === 10 ? 
            epochTimestamp * 1000 : epochTimestamp;
          
          return format(new Date(timestamp), 'dd MMM yyyy');
        } catch (err) {
          console.error('Error formatting expiry date:', err);
          return epochTimestamp;  // Return original value if parsing fails
        }
      };
      const fetchMarketWatches = async () => {
        try {
          const response = await primusAPI({
            endpoint: "/api/v1/marketwatches",
            method: "GET",
          });
      
          if (response.status === "success") {
            const subscribedInstruments = new Set();
      
            const processedMarketWatches = response.data.map((mw) => ({
              id: mw.id,
              login_id: mw.login_id,
              name: mw.name,
              instruments: mw.instruments.map((inst) => {
                const exchangeCode = EXCHANGE_CODES[inst.exchange];
                const key = `${exchangeCode}_${inst.token}`;
      
                // if (!subscribedInstruments.has(key)) {
                //   marketDataSubscriber.subscribe(exchangeCode, inst.token);
                //   subscribedInstruments.add(key);
                // }
      
                // const latestData = marketDataStore.getMarketData(exchangeCode, inst.token);
      
                return {
                  ...inst,
                  exchangeCode,
                  // ltp: latestData ? latestData.ltp / 100 : 0,
                  // change: latestData ? latestData.change / 100 : 0,
                  // changePercent: latestData ? latestData.changePercent : 0,
                  // open: latestData ? latestData.open / 100 : 0,
                  // high: latestData ? latestData.high / 100 : 0,
                  // low: latestData ? latestData.low / 100 : 0,
                  // close: latestData ? latestData.close / 100 : 0,
                  // volume: latestData ? latestData.volume : 0,
                  // bestBid: latestData
                  //   ? {
                  //       price: latestData.bestBid.price / 100,
                  //       quantity: latestData.bestBid.quantity,
                  //     }
                  //   : { price: 0, quantity: 0 },
                  // bestAsk: latestData
                  //   ? {
                  //       price: latestData.bestAsk.price / 100,
                  //       quantity: latestData.bestAsk.quantity,
                  //     }
                  //   : { price: 0, quantity: 0 },
                  // atp: latestData ? latestData.atp / 100 : 0,
                };
              }),
            }));
      
            setMarketWatches(processedMarketWatches);
          } else {
            setError(response.message || "Failed to fetch market watches");
          }
        } catch (err) {
          setError("Error loading market watches");
          console.error("MarketWatch fetch error:", err);
        } finally {
        //   setLoading(false);
        }
      };
      const handleSearch = async (value) => {
        setSearchQuery(value);
        
        if (value.length < 2) {
          setSearchResults([]);
          return;
        }
    
        setSearching(true);
        try {
          const token = localStorage.getItem('auth_token');
          const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.SEARCH_INSTRUMENTS)}?key=${encodeURIComponent(value)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-authorization-token': token,
              'x-device-type': 'WEB',
              'x-device-id': getDeviceId()
            }
          });
    
          const data = await response.json();
          if (!data.error || data.error.code === 0) {  // Check error structure
            setSearchResults(data.result || []);
          } else {
            console.error('Search failed:', data.error.message);
          }
        } catch (err) {
          console.error('Search error:', err);
        } finally {
          setSearching(false);
        }
      };

      // Check if an instrument is already in the watchlist
      const isInstrumentInWatchlist = (instrument) => {
        if (!currentWatchlist || !currentWatchlist.instruments) return false;
        
        return currentWatchlist.instruments.some(
          (item) => item.token === instrument.token && item.exchange === instrument.exchange
        );
      };

      // Handle click on search result
      const handleSearchResultClick = (result) => {
        // Check if the instrument is already in the watchlist
        if (isInstrumentInWatchlist(result)) {
          // Highlight the existing instrument
          setHighlightedInstrument(result);
          showNotification('INFO', {
            message: `${result.display_name} is already in your watchlist`
          });
        } else {
          // Add the instrument to the watchlist
          addInstrument(result);
        }
        
        // Clear search
        setSearchQuery('');
        setSearchResults([]);
      };
      
        useEffect(() => {
          const timer = setTimeout(() => {
            handleSearch(searchQuery);
          }, 300);
      
          return () => clearTimeout(timer);
        }, [searchQuery]);

      
  return (
    <Box
      sx={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 1,
        backgroundColor: 'background.paper'
      }}
    >
      <Box
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'action.hover',
          borderRadius: 0,
          position: 'relative'  // For search results positioning
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', ml: 1 }} />
        <TextField
          placeholder="Search symbols..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="standard"
          fullWidth
          sx={{ 
            '& .MuiInput-root': {
              padding: '8px'
            },
            '& .MuiInput-root:before, & .MuiInput-root:after': {
              display: 'none'
            }
          }}
        />
        {searching && (
          <CircularProgress size={20} sx={{ mr: 1 }} />
        )}
        {!isExpandedView && (
          <Tooltip title="Expand Watchlist">
            <IconButton 
              size="small" 
              onClick={() => navigate('/marketwatch-expanded')}
              sx={{ mr: 1 }}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Search Results Dropdown */}
      {searchResults.length > 0 && searchQuery.length >= 2 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: "300px",
            overflow: 'auto',
            zIndex: 1300,
            backgroundColor: 'background.paper'
          }}
        >
          {searchResults.map((result) => {
            const alreadyInWatchlist = isInstrumentInWatchlist(result);
            
            return (
              <Box
                key={result.token}
                onClick={() => handleSearchResultClick(result)}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  backgroundColor: alreadyInWatchlist ? 'action.selected' : 'inherit',
                  '&:hover': {
                    backgroundColor: alreadyInWatchlist ? 'action.selected' : 'action.hover'
                  },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: '48px'
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box component="span"
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: alreadyInWatchlist ? 'bold' : 'medium',
                      fontSize: '0.875rem',
                      lineHeight: 1.2
                    }}
                  >
                    {result.display_name}
                    <ExchangeTag exchange={result.exchange} />
                    {alreadyInWatchlist && (
                      <Box component="span" 
                        sx={{ 
                          ml: 1, 
                          fontSize: '0.75rem',
                          color: 'primary.main',
                          fontWeight: 'medium'
                        }}
                      >
                        (Already in watchlist)
                      </Box>
                    )}
                  </Box>
                  <Box component="span"
                    sx={{ 
                      display: 'block',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      lineHeight: 1.2
                    }}
                  >
                    {result.exchange === 'CDS' ? result.company : result.trading_symbol}
                  </Box>
                </Box>
                <Box
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-end'
                  }}
                >
                  {/* {(!result.execution || result.execution !== 'SPREAD') && (
                    <Box component="span"
                      sx={{ 
                        fontWeight: 'medium',
                        fontSize: '0.875rem',
                        lineHeight: 1.2
                      }}
                    >
                      ₹ {result.close_price}
                    </Box>
                  )} */}
                  {result.expiry && 
                   (!result.execution || result.execution !== 'SPREAD') && (
                    <Box component="span"
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      Expiry: {formatExpiryDate(result.expiry)}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar;
